import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Trash2, Lock, Send, Compass } from "lucide-react";
import { api, formatApiError, fmtUSD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n/LanguageContext";
import { Label, Input, PrimaryButton, GhostButton } from "@/components/fields";

const strip = (items) => items.map(({ uid, label, image, ...rest }) => rest);

export default function CartPage() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const { items, cartId, removeItem, updateItem, clearCart } = useCart();
  const [check, setCheck] = useState(null);
  const [phone, setPhone] = useState("");
  const [paying, setPaying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!items.length) {
      setCheck(null);
      return;
    }
    let cancelled = false;
    api.post("/cart/validate", { items: strip(items), cart_id: cartId })
      .then((r) => { if (!cancelled) setCheck(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [items]);

  const rows = items.map((it, i) => ({ it, v: check?.items?.[i] }));
  const payableTotal = check?.payable_total ?? 0;
  const hasInvalid = rows.some(({ v }) => v && !v.ok);
  const hasRequests = rows.some(({ v }) => v && v.ok && !v.payable);

  const checkout = async () => {
    if (!user) {
      toast.error(t("cart.signin"));
      navigate("/auth", { state: { from: "/cart" } });
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/payments/cart-checkout", {
        cart_id: cartId,
        items: strip(items),
        origin_url: window.location.origin,
        phone,
        lang,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      clearCart();
      toast.success(t("cart.requestsSent"));
      navigate("/account");
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-14 sm:py-20" data-testid="cart-page">
      <div className="flex items-center gap-3 mb-8">
        <ShoppingBag size={26} className="text-terracotta" />
        <h1 className="font-serif text-4xl text-ink">{t("cart.title")}</h1>
        <span className="text-sm text-ink/50 font-mono" data-testid="cart-count-label">{items.length}</span>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 space-y-4" data-testid="cart-empty">
          <p className="font-serif text-2xl text-ink">{t("cart.empty")}</p>
          <p className="text-sm text-ink/55">{t("cart.emptySub")}</p>
          <Link to="/excursions" data-testid="cart-browse-link" className="inline-block mt-2">
            <PrimaryButton><Compass size={16} /> {t("cart.browse")}</PrimaryButton>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map(({ it, v }, idx) => (
            <div key={it.uid || `${it.kind}-${idx}`} data-testid={`cart-item-${it.uid || idx}`}
                 className="rounded-2xl border border-ink/10 bg-white p-4 sm:p-5 flex gap-4 items-start">
              {it.image && <img src={it.image} alt="" className="w-14 h-14 rounded-xl object-cover hidden sm:block" />}
              <div className="grow space-y-1.5">
                <p className="font-semibold text-ink leading-snug" data-testid="cart-item-label">{it.label}</p>
                <p className="text-xs text-ink/55" data-testid="cart-item-detail">{v?.detail || ""}</p>
                {v && !v.ok && (
                  <p className="text-xs font-semibold text-destructive" data-testid="cart-item-error">
                    {t("cart.unavailable")}: {v.reason}
                  </p>
                )}
                {v?.ok && v.uplift_percent > 0 && (
                  <p className="text-xs text-gold" data-testid="cart-item-uplift">{v.season} +{v.uplift_percent}%</p>
                )}
                {it.kind === "villa" ? (
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <Input data-testid="cart-item-checkin" type="date" value={it.check_in} className="!w-36 !py-1"
                           onChange={(e) => updateItem(it.uid, { check_in: e.target.value })} />
                    <span className="text-ink/40 text-xs">→</span>
                    <Input data-testid="cart-item-checkout" type="date" value={it.check_out} className="!w-36 !py-1"
                           onChange={(e) => updateItem(it.uid, { check_out: e.target.value })} />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 pt-1">
                    <Input data-testid="cart-item-date" type="date" value={it.date || ""} className="!w-36 !py-1"
                           onChange={(e) => updateItem(it.uid, { date: e.target.value })} />
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <Label htmlFor={`g-${it.uid}`}>{t("cart.guests")}</Label>
                  <Input id={`g-${it.uid}`} data-testid="cart-item-guests" type="number" min="1" max={it.kind === "villa" ? 6 : 20}
                         value={it.guests} className="!w-20 !py-1"
                         onChange={(e) => updateItem(it.uid, { guests: Math.max(1, Number(e.target.value) || 1) })} />
                </div>
              </div>
              <div className="text-right shrink-0 space-y-2">
                {v?.ok && v.payable && (
                  <p className="font-serif text-xl text-terracotta" data-testid="cart-item-price">${fmtUSD(v.total)}</p>
                )}
                {v?.ok && !v.payable && (
                  <span className="inline-block rounded-full bg-teal/10 text-teal text-[11px] font-semibold px-3 py-1" data-testid="cart-item-badge">
                    {it.kind === "villa" ? t("cart.approval") : t("cart.quote")}
                  </span>
                )}
                <div>
                  <button data-testid="cart-item-remove" onClick={() => { removeItem(it.uid); toast.success(t("cart.removed")); }}
                          className="text-ink/35 hover:text-destructive transition-colors" aria-label="Remove">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          <div className="rounded-3xl bg-night text-sand p-6 space-y-3" data-testid="cart-summary">
            <div className="flex items-center justify-between">
              <span className="text-sm text-sand/60 font-mono uppercase tracking-[0.15em]">{t("cart.subtotal")}</span>
              <span className="font-serif text-3xl text-gold" data-testid="cart-total">${fmtUSD(payableTotal)}</span>
            </div>
            <p className="text-xs text-sand/50">{t("cart.feesNote")}</p>
            {hasRequests && <p className="text-xs text-gold/90" data-testid="cart-requests-note">{t("cart.requestsNote")}</p>}
            <div className="pt-1">
              <Label htmlFor="cart-phone">{t("cart.phone")}</Label>
              <Input id="cart-phone" data-testid="cart-phone-input" value={phone} placeholder="+1 ..."
                     onChange={(e) => setPhone(e.target.value)} className="!bg-white/10 !border-white/20 !text-sand" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-between pt-2">
            <Link to="/excursions" data-testid="cart-continue-shopping">
              <GhostButton className="w-full sm:w-auto">{t("cart.continueShopping")}</GhostButton>
            </Link>
            <PrimaryButton data-testid="cart-checkout-btn" onClick={checkout} disabled={paying || hasInvalid}>
              {paying ? t("cart.checkingOut") : payableTotal > 0
                ? <><Lock size={15} /> {t("cart.checkout")} — ${fmtUSD(payableTotal)}</>
                : <><Send size={15} /> {t("cart.sendRequests")}</>}
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
}
